import { useCallback, useRef, useState } from 'react';
import {
  PeranAsisten_Assistant,
  PeranAsisten_User,
  PeristiwaAsisten_Alat,
  PeristiwaAsisten_Galat,
  PeristiwaAsisten_Selesai,
  PeristiwaAsisten_Teks,
  PeristiwaAsisten_Tiket,
} from '@/constants/asisten';
import { asistenService } from '../asistenService';
import type { PeristiwaAsisten, PesanChat, StatusAsisten } from '../types';

let nomorKunci = 0;
const kunciBaru = () => `p${++nomorKunci}`;

/**
 * State & aksi percakapan Asisten — kirim (mengalir), berhenti, percakapan baru, muat riwayat, rating.
 */
export function useAsistenChat() {
  const [pesan, setPesan] = useState<PesanChat[]>([]);
  const [idPercakapan, setIdPercakapan] = useState<string | undefined>();
  const [sedangMenjawab, setSedangMenjawab] = useState(false);
  const [keteranganAlat, setKeteranganAlat] = useState<string | null>(null);
  const [status, setStatus] = useState<StatusAsisten | null>(null);
  const pembatal = useRef<AbortController | null>(null);

  /** Ubah pesan asisten terakhir (yang sedang mengalir). */
  const ubahTerakhir = (ubah: (p: PesanChat) => PesanChat) =>
    setPesan((daftar) => {
      const salinan = [...daftar];
      salinan[salinan.length - 1] = ubah(salinan[salinan.length - 1]);
      return salinan;
    });

  const muatStatus = useCallback(async () => {
    try {
      setStatus(await asistenService.getStatus());
    } catch {
      setStatus({ tersedia: false, alasan: 'Asisten tidak dapat dihubungi.', sisaKuotaHariIni: 0 });
    }
  }, []);

  const kirim = useCallback(async (teks: string, menuAsal: string) => {
    const isi = teks.trim();
    if (!isi || sedangMenjawab) return;

    setPesan((daftar) => [
      ...daftar,
      { kunci: kunciBaru(), peran: PeranAsisten_User, isi },
      { kunci: kunciBaru(), peran: PeranAsisten_Assistant, isi: '' },
    ]);
    setSedangMenjawab(true);
    const kontrol = new AbortController();
    pembatal.current = kontrol;

    const terima = (ev: PeristiwaAsisten) => {
      switch (ev.jenis) {
        case PeristiwaAsisten_Teks:
          setKeteranganAlat(null);
          ubahTerakhir((p) => ({ ...p, isi: p.isi + (ev.teks ?? '') }));
          break;
        case PeristiwaAsisten_Alat:
          setKeteranganAlat(ev.teks ?? null);
          break;
        case PeristiwaAsisten_Tiket:
          ubahTerakhir((p) => ({ ...p, tiket: ev.tiket }));
          break;
        case PeristiwaAsisten_Selesai:
          if (ev.idPercakapan) setIdPercakapan(ev.idPercakapan);
          ubahTerakhir((p) => ({ ...p, idPesan: ev.idPesan, sumber: ev.sumber }));
          break;
        case PeristiwaAsisten_Galat:
          ubahTerakhir((p) => ({ ...p, isi: p.isi ? `${p.isi}\n\n${ev.teks ?? ''}` : (ev.teks ?? ''), galat: true }));
          break;
      }
    };

    try {
      await asistenService.kirimPesan({ pesan: isi, idPercakapan, menuAsal }, terima, kontrol.signal);
    } catch (e) {
      if (!kontrol.signal.aborted) {
        terima({ jenis: PeristiwaAsisten_Galat, teks: e instanceof Error ? e.message : 'Asisten tidak dapat dihubungi.' });
      }
    } finally {
      setSedangMenjawab(false);
      setKeteranganAlat(null);
      pembatal.current = null;
      void muatStatus();
    }
  }, [idPercakapan, sedangMenjawab, muatStatus]);

  const berhenti = useCallback(() => pembatal.current?.abort(), []);

  const percakapanBaru = useCallback(() => {
    pembatal.current?.abort();
    setPesan([]);
    setIdPercakapan(undefined);
  }, []);

  const muatPercakapan = useCallback(async (id: string) => {
    pembatal.current?.abort();
    const detail = await asistenService.getPercakapan(id);
    setIdPercakapan(detail.idPercakapan);
    setPesan(detail.pesan.map((p) => ({
      kunci: kunciBaru(), peran: p.peran, isi: p.isi, idPesan: p.idPesan, rating: p.rating,
    })));
  }, []);

  const beriRating = useCallback(async (idPesan: number, rating: number, alasan?: string) => {
    await asistenService.beriRating(idPesan, rating, alasan);
    setPesan((daftar) => daftar.map((p) => (p.idPesan === idPesan ? { ...p, rating } : p)));
  }, []);

  const tambahTiket = useCallback((isi: string, tiket: PesanChat['tiket']) => {
    setPesan((daftar) => [...daftar, { kunci: kunciBaru(), peran: PeranAsisten_Assistant, isi, tiket }]);
  }, []);

  return {
    pesan, idPercakapan, sedangMenjawab, keteranganAlat, status,
    muatStatus, kirim, berhenti, percakapanBaru, muatPercakapan, beriRating, tambahTiket,
  };
}
